import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [over18, setOver18] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (ref) sessionStorage.setItem("coachiq_ref", ref); }, [ref]);

  const canSubmit = name && email && password && over18 && authority && terms && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!over18 || !authority || !terms) {
      toast.error("Please confirm all three items to create your account.");
      return;
    }
    setBusy(true);
    try {
      await signup(name, email, password, ref || sessionStorage.getItem("coachiq_ref"), {
        over_18_confirmed: over18,
        authority_confirmed: authority,
        terms_accepted: terms,
      });
      toast.success("Welcome to CoachIQ · 30-day trial started");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/8941613/pexels-photo-8941613.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/75 to-[#007AFF]/85" />
        <div className="relative p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-9 h-9 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"><Sparkles className="w-5 h-5" strokeWidth={2.5} /></div>
            <span className="font-display font-bold text-lg">CoachIQ</span>
          </Link>
          <div>
            <div className="font-display text-3xl font-bold leading-tight">Start the season<br/>feeling prepared.</div>
            <div className="mt-3 text-white/80 max-w-sm">Create your account and set up your first team.</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm animate-slide-up" data-testid="signup-form">
          <div className="mb-8">
            <div className="font-display text-3xl font-bold tracking-tight">Create your account</div>
            <div className="text-gray-500 mt-1">30-day Coach Pro trial. No card needed.</div>
            <div className="mt-2 text-xs text-gray-400" data-testid="signup-uphaus-tag">CoachIQ · Built by <a href="https://uphauslabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007AFF] font-semibold">UPHAUS Labs</a></div>
            <div className="mt-2 text-[11px] text-gray-400" data-testid="signup-adult-notice">CoachIQ is for adult coaches and authorized team staff. Not directed to children under 18.</div>
            {ref && <div className="mt-3 inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-1 text-xs font-semibold" data-testid="signup-ref-banner"><Check className="w-3 h-3" /> Invited by <span className="font-mono">{ref}</span></div>}
          </div>

          <label className="text-sm font-medium text-gray-700">Your name</label>
          <input data-testid="signup-name" required value={name} onChange={e=>setName(e.target.value)}
            className="mt-1.5 mb-4 w-full h-11 px-4 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none transition-colors bg-white" />

          <label className="text-sm font-medium text-gray-700">Email</label>
          <input data-testid="signup-email" required type="email" value={email} onChange={e=>setEmail(e.target.value)}
            className="mt-1.5 mb-4 w-full h-11 px-4 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none transition-colors bg-white" />

          <label className="text-sm font-medium text-gray-700">Password</label>
          <input data-testid="signup-password" required type="password" minLength={6} value={password} onChange={e=>setPassword(e.target.value)}
            className="mt-1.5 mb-4 w-full h-11 px-4 rounded-2xl border border-gray-200 focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/20 outline-none transition-colors bg-white" />

          <div className="mt-2 mb-5 space-y-2.5 text-[13px] leading-snug text-gray-700" data-testid="signup-consent-block">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input data-testid="signup-consent-over18" type="checkbox" checked={over18} onChange={e => setOver18(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
              <span>I confirm I am at least <span className="font-semibold">18 years old</span>.</span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input data-testid="signup-consent-authority" type="checkbox" checked={authority} onChange={e => setAuthority(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
              <span>I confirm I have <span className="font-semibold">authority</span> to enter information about the players and teams I coach, and I will only enter the minimum information needed for coaching.</span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input data-testid="signup-consent-terms" type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
              <span>I accept the <Link to="/terms" target="_blank" className="text-[#007AFF] font-semibold hover:underline">Terms</Link> and <Link to="/privacy" target="_blank" className="text-[#007AFF] font-semibold hover:underline">Privacy Policy</Link>.</span>
            </label>
          </div>

          <button data-testid="signup-submit" disabled={!canSubmit} type="submit" className="w-full h-11 rounded-full bg-[#007AFF] hover:bg-[#0056B3] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold transition-colors shadow-lg shadow-blue-500/20">
            {busy ? "Creating…" : "Create account"}
          </button>

          <div className="my-4 flex items-center gap-3 text-xs text-gray-400">
            <div className="flex-1 h-px bg-gray-200" /> or <div className="flex-1 h-px bg-gray-200" />
          </div>

          <GoogleSignInButton label="Sign up with Google" testid="signup-google-btn" />
          <div className="mt-2 text-[11px] text-gray-400" data-testid="signup-google-consent-note">You'll be asked to confirm age & authority after Google sign-in.</div>

          <div className="mt-6 text-sm text-gray-500 text-center">
            Already have one? <Link to="/login" className="text-[#007AFF] font-semibold hover:underline" data-testid="signup-goto-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
